import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../components/config/appConfig";
import { PROJECT_ROLES, roleErrorMessage } from "../utilities/projectRoles";

/**
 * The roles a project uses, read from the database.
 *
 * Roles belong to one project, so every call is scoped by project id. While
 * the first request is in flight the hook serves PROJECT_ROLES, the same set
 * the server seeds a project with — the member table and the invite dialog
 * therefore never render an empty dropdown.
 */
const rolesUrl = (projectId) => `${API_BASE_URL}/projectroles/${projectId}`;

/* changing a project's roles is limited to its creator, so writes say who is asking */
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } });

export default function useProjectRoles(projectId) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchRoles = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const response = await axios.get(rolesUrl(projectId));
      setRoles(Array.isArray(response?.data) ? response.data : []);
      setLoaded(true);
      setError(null);
    } catch (err) {
      /* Callers fall back to PROJECT_ROLES so no dropdown renders empty, but a
         failed load must not read as "this project has no roles" — the manage
         dialog needs to tell the two apart. */
      console.log(err, "could not load project roles");
      setRoles([]);
      setLoaded(false);
      setError(roleErrorMessage(err, "Could not load this project's roles."));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const createRole = useCallback(
    async (name, description = "") => {
      const response = await axios.post(rolesUrl(projectId), { name, description }, authConfig());
      await fetchRoles();
      return response?.data;
    },
    [projectId, fetchRoles]
  );

  const renameRole = useCallback(
    async (roleId, name, description) => {
      const response = await axios.put(`${rolesUrl(projectId)}/${roleId}`, { name, description }, authConfig());
      await fetchRoles();
      return response?.data;
    },
    [projectId, fetchRoles]
  );

  const deleteRole = useCallback(
    async (roleId) => {
      const response = await axios.delete(`${rolesUrl(projectId)}/${roleId}`, authConfig());
      await fetchRoles();
      return response?.data;
    },
    [projectId, fetchRoles]
  );

  /* Names only — what the member table, the role filter and the invite dialog
     actually bind to. Falls back until the first response lands. */
  const roleNames = useMemo(() => {
    if (!loaded || roles.length === 0) return PROJECT_ROLES;
    return roles.map((role) => role?.name).filter(Boolean);
  }, [roles, loaded]);

  /* Shape SelectMenuTextField expects. It reads `_id` first when present, and
     the value stored against a member is the name, so these carry no id. */
  const roleOptions = useMemo(
    () => roleNames.map((name) => ({ name, value: name })),
    [roleNames]
  );

  return {
    roles,
    roleNames,
    roleOptions,
    loading,
    loaded,
    error,
    refresh: fetchRoles,
    createRole,
    renameRole,
    deleteRole,
  };
}
